import { Button, Grid } from "@mui/material";
import { useMemo, useState } from "react";
import FilterChips from "../../../App/Components/Filters/FilterChips";
import SearchFilters from "../../../App/Components/Filters/SearchFilters";
import CSVImportModal from "../../../App/Components/ImportCSV/CSVImportModal";
import Modal from "../../../App/Components/Modal/Modal";
import { useFilteredGroupMembersPagingQuery } from "../../../App/Queries/GroupQueries";
import { useGroupCandidatesFilterStore } from "../../../App/State/Stores/GroupCandidatesPaginationStore";
import { ICandidate } from "../../../App/Typings/Candidate";
import { ICandidateProfile } from "../../../App/Typings/CandidateProfile";
import CandidateTags from "../../../CandidateProfile/Components/CandidateAccordions/Subcomponents/CandidateTags";
import BulkTagModal from "../../../ManageCandidates/Components/BulkTagModal/BulkTagModal";
import MessageDialogCandidateEmail from "../../../MessageComponents/MessageDialog/MessageDialogCandidateEmail";
import MessageDialogCandidateText from "../../../MessageComponents/MessageDialog/MessageDialogCandidateText";
import { MemoMessageDialogGroupTabs } from "../../../MessageComponents/MessageDialog/MessageDialogGroupTabs";
import { useGroupCandidateSelectionContext } from "../../State/GroupCandidateSelectionContext";
import { useGroupProfileContext } from "../../State/GroupProfileContext";
import RemoveGroupMemberState from "../../State/RemoveGroupMemberState";
import { GroupTable } from "../GroupTable/GroupTable";
import GroupTableButtons from "../GroupTable/GroupTableButtons";
import { MoveToGroup } from "../MoveToGroup/MoveToGroup";
import { RemoveCandidateDialog } from "../RemoveCandidateDialog/RemoveCandidateDialog";

interface IGroupCandidateListingProps {
  isTableVisible: boolean;
}

export default function GroupCandidateListing({ isTableVisible }: IGroupCandidateListingProps) {
  const { group, isLoading } = useGroupProfileContext();
  const [editTagsModalOpen, setEditTagsModalOpen] = useState<boolean>(false);
  const [tagEditCandidate, setTagEditCandidate] = useState<ICandidate>();
  const [isMoveCandidateModalOpen, setIsMoveCandidateModalOpen] = useState<boolean>(false);

  const { filteredCandidateList, filterQueryString } = useFilteredGroupMembersPagingQuery(
    group.id,
    isTableVisible,
  );
  const { tags, timeFilter, handleTagsChange, handleTimeFilterChange } = useGroupCandidatesFilterStore(
    group.id,
  );

  const openModal = (candidate: ICandidate) => {
    setTagEditCandidate(candidate);
    setEditTagsModalOpen(true);
  };

  const {
    getters: { currentPageSelectedIds, selectAll, excludedCandidateIds },
  } = useGroupCandidateSelectionContext();

  // Compute current page selected candidates based on selection state
  const currentPageSelectedCandidates = useMemo(() => {
    if (selectAll) {
      // When selectAll is true, all current page candidates are selected except excluded ones
      return filteredCandidateList.filter((c) => !excludedCandidateIds.includes(c.id));
    }
    // Otherwise, only explicitly selected candidates from current page
    return filteredCandidateList.filter((c) => currentPageSelectedIds.includes(c.id));
  }, [filteredCandidateList, selectAll, excludedCandidateIds, currentPageSelectedIds]);

  const {
    getters: { confirmRemoveDialogOpen, candidateToRemove },
    handlers: {
      handleCloseConfirmRemoveDialog,
      handleRemoveCandidateFromGroup,
      handleSelectCandidateToRemove,
    },
  } = RemoveGroupMemberState(group.id, filteredCandidateList);

  return (
    <>
      <Grid container item xs={12} sx={{ marginBottom: "10px" }}>
        <Grid container item xs={12} md={8} spacing={2}>
          <SearchFilters
            isLoading={isLoading}
            filterQueryString={filterQueryString}
            setTags={handleTagsChange}
            setTimeFilter={handleTimeFilterChange}
            tags={tags}
            timeFilter={timeFilter}
            id="groupprofile"
          />
        </Grid>
        <Grid container item xs={12} md={4} spacing={2}>
          {!isLoading && <GroupTableButtons setIsMoveCandidateModalOpen={setIsMoveCandidateModalOpen} />}
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ marginBottom: "10px" }}>
        <FilterChips
          timeFilter={timeFilter}
          tags={tags}
          handleTimeFilterChange={handleTimeFilterChange}
          handleTagsChange={handleTagsChange}
        />
      </Grid>
      <Grid container item xs={12}>
        <BulkTagModal
          selectedCandidates={currentPageSelectedCandidates.map((c) => c.id)}
          selectedCount={currentPageSelectedCandidates.length}
        />
        <GroupTable
          isTableVisible={isTableVisible}
          onEditTagsClick={openModal}
          handleSelectCandidateToRemove={handleSelectCandidateToRemove}
        />
      </Grid>
      <RemoveCandidateDialog
        confirmRemoveDialogOpen={confirmRemoveDialogOpen}
        handleCloseConfirmRemoveDialog={handleCloseConfirmRemoveDialog}
        handleRemoveCandidateFromGroup={handleRemoveCandidateFromGroup}
        candidateToRemove={candidateToRemove}
        groupName={group.name}
      />
      <MessageDialogCandidateEmail />
      <MessageDialogCandidateText />
      <MemoMessageDialogGroupTabs key={group.id} group={group} />
      <CSVImportModal groupId={group.id} />
      <Modal
        content={
          <CandidateTags candidate={tagEditCandidate as ICandidateProfile} optimisticLoading={false} />
        }
        title="Edit Candidate Tags"
        dataTestId="candidate-tag-edit"
        footerContent={
          <Button color="secondary" variant="contained" onClick={() => setEditTagsModalOpen(false)}>
            Close
          </Button>
        }
        isOpen={editTagsModalOpen}
        handleCloseModal={() => setEditTagsModalOpen(false)}
        size="md"
      />
      <MoveToGroup
        groupId={group.id}
        isModalOpen={isMoveCandidateModalOpen}
        setModalOpen={setIsMoveCandidateModalOpen}
      />
    </>
  );
}
